import { type ReactNode } from 'react'
import { FormContainer } from '@/components/FormContainer'
import { PersonalInfoEditor } from '@/features/tax-profile/individual-profiles/components/PersonalInfoEditor'
import type { SpousalProfileEditorViewModel } from '@/features/tax-profile/individual-profiles/SpousalProfileEditorViewModel'
import { TextFieldContainer } from '@/components/inputs/TextFieldContainer'
import { FormFieldSet, FormFieldSetLegend } from '@/components/Form'
import { observer } from 'mobx-react-lite'
import { HeadsUpBlock } from '@/components/HeadsUpBlock'
import { IsSkeletonContext } from '@/components/Skeleton'

export interface SpousalProfileEditorProps {
  children?: ReactNode
  formId?: string
  isDisabled?: boolean
  isSkeleton?: boolean
  vm: SpousalProfileEditorViewModel
  privacyDialogTestId?: string
  privacyDialogCloseButtonTestId?: string
}

/**
 * Form for editing a spousal profile.
 */
export const SpousalProfileEditor = observer(function SpousalProfileEditor({
  formId,
  isDisabled,
  isSkeleton,
  vm,
  privacyDialogTestId,
  privacyDialogCloseButtonTestId,
}: SpousalProfileEditorProps) {
  const testId = 'spousal-profile-editor'
  return (
    <IsSkeletonContext value={isSkeleton}>
      <FormContainer
        id={formId}
        onSubmit={vm.save}
        disabled={isDisabled || vm.save.pending}
        testId={testId}
      >
        {vm.isPrefilledUsingLatest && (
          <HeadsUpBlock title="Existing information found">
            It looks like we already have some of your spouse's information on
            record, so we've pre-filled it below.
          </HeadsUpBlock>
        )}
        <FormFieldSet>
          <FormFieldSetLegend>Spouse's personal information</FormFieldSetLegend>
          <PersonalInfoEditor
            vm={vm.personalInfoVm}
            privacyDialogTestId={privacyDialogTestId}
            privacyDialogCloseButtonTestId={privacyDialogCloseButtonTestId}
            testId={testId}
          />
          <TextFieldContainer
            model={vm}
            field="occupation"
            label="Occupation (optional)"
            placeholder="e.g. Teacher"
            testId={testId ? `${testId}-occupation-input` : undefined}
          />
        </FormFieldSet>
      </FormContainer>
    </IsSkeletonContext>
  )
})
